import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./tbid.html";
import { clearAuthenticationContext, getAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { DEFAULT_API_TIMEOUT, IIS_ENV, NODE_ENV, TBID_URL } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { generateBaseUrl, generateTbidLoginUrl, generateTbidSignUpUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { isLoggedIn } from '~/modules/services/authService';
import { userStore } from '~/modules/stores/userStore';
import { appendMetaTag, removeMetaTag } from '~/modules/utilities/browser';
import { createCookie, getCookie } from '~/modules/utilities/cookies';
import { handleException } from '~/modules/utilities/error_utilities';
import { logger } from '~/modules/utilities/logger';
const IIS_V2_EVENT_NAMES = ['onInit', 'onLogin', 'onLogout'];
const SID_COOKIE_NAME = 'sid';
function isAuthenticatedOrgUser(user) {
  return user?.isGuestUser === false;
}
function isIisV2EventName(eventName) {
  return IIS_V2_EVENT_NAMES.some(candidate => candidate === eventName);
}

// v1: name of functions registered on window and called by authProviderEmbeddedLogin_v1_3
const TBID_INIT_HANDLER = 'onInitHandler';
const TBID_LOGIN_HANDLER = 'onLoginHandler';
const TBID_LOGOUT_HANDLER = 'onLogoutHandler';

// v1: widget path loaded from the TBID community org
const TBID_AUTH_WIDGET_PATH = '/resource/authProviderEmbeddedLogin_v1_3';
const TBID_SCRIPT_LOAD_TIMEOUT = 10000;
const IIS_TIMEOUT_LOG_MESSAGE = 'IIS JS v2 timeout; preserving last confirmed auth state';
const TBID_INIT_TIMEOUT = 8000;
const TBID_MAX_RETRIES = 2;
const TBID_RETRY_DELAY_MS = 1000;
const TBID_LOAD_TIMEOUT = 'TBID_LOAD_TIMEOUT';
const TBID_LOAD_ERROR = 'TBID_LOAD_ERROR';
let tbidTimeoutId;
let autoLoginFrame;
let v1AbortController = null;
class Tbid extends LightningElement {
  constructor(...args) {
    super(...args);
    this.usingV2 = false;
    this.iisAuthResolved = false;
    this.iisAuthInFlight = false;
    this.iisAutoLoginFrame = null;
    this.iisAuthTransaction = 0;
    this.iisConnectionGeneration = 0;
    this.onIisV2Event = event => this.handleIisV2Event(event);
  }
  // stable ref: add/removeEventListener must receive the same function identity on remount

  connectedCallback() {
    this.iisConnectionGeneration += 1;
    this.usingV2 = FeatureFlags.FF_IIS_JS_V2;
    if (this.usingV2) {
      this.initIisV2();
    } else {
      initV1();
    }
  }
  disconnectedCallback() {
    this.iisConnectionGeneration += 1;
    if (this.usingV2) {
      document.removeEventListener('tbid_auth_event', this.onIisV2Event);
      this.invalidateIisAuthTransaction();
    } else {
      v1AbortController?.abort();
      v1AbortController = null;
      if (tbidTimeoutId) {
        clearTimeout(tbidTimeoutId);
      }
      removeMetaTag('salesforce-cache-max-age');
      removeMetaTag('salesforce-brand');
      removeMetaTag('salesforce-community');
      removeMetaTag('salesforce-mode');
      removeMetaTag('salesforce-redirect-uri');
      removeMetaTag('salesforce-authprovider-login');
      removeMetaTag('salesforce-authprovider-signup');
      removeMetaTag('salesforce-init-handler');
      removeMetaTag('salesforce-login-handler');
      removeMetaTag('salesforce-logout-handler');
    }
  }

  // ─── IIS JS v2 ─────────────────────────────────────────────────────────────

  initIisV2() {
    const connectionGeneration = this.iisConnectionGeneration;
    if (IIS_ENV === '') {
      logger.warn('IIS JS v2: IIS_ENV is not set — SDK may fail to initialize');
    }
    // tbidAuthConfig must be set before the script loads
    window.tbidAuthConfig = {
      env: IIS_ENV
    };
    this.iisAuthResolved = false;
    logger.debug(`IIS JS v2: initializing with env="${IIS_ENV}"`);

    // Event listener must be registered before the script loads so no events are missed
    document.addEventListener('tbid_auth_event', this.onIisV2Event);
    this.armIisInitializationWatchdog(TBID_SCRIPT_LOAD_TIMEOUT, 'IIS SDK loading');
    logger.debug('IIS JS v2: loading SDK from https://c.salesforce.com/digital/iis/v2-stable/iis.js');
    loadIisScript('https://c.salesforce.com/digital/iis/v2-stable/iis.js').then(() => {
      if (connectionGeneration !== this.iisConnectionGeneration) {
        return;
      }
      logger.debug('IIS JS v2: SDK loaded successfully');
      if (this.isConnected && !this.iisAuthResolved) {
        this.armIisInitializationWatchdog(TBID_INIT_TIMEOUT, 'first IIS authentication event');
      }
    }).catch(error => {
      if (connectionGeneration !== this.iisConnectionGeneration) {
        return;
      }
      this.clearIisInitializationWatchdog();
      handleException(error, 'tbid.initIisV2.loadSDK');
      if (this.isConnected && !this.iisAuthResolved) {
        this.iisAuthResolved = true;
        logger.debug('IIS JS v2: no auth event received before SDK failure — preserving last confirmed auth state');
        this.settleIisUnavailable();
      }
    });
  }
  invalidateIisAuthTransaction() {
    this.iisAuthTransaction += 1;
    this.iisAuthInFlight = false;
    this.iisAuthUserId = undefined;
    this.clearIisInitializationWatchdog();
    this.clearIisAutoLoginFrame();
  }
  clearIisAuthTimeout() {
    if (this.iisAutoLoginTimeoutId !== undefined) {
      clearTimeout(this.iisAutoLoginTimeoutId);
      this.iisAutoLoginTimeoutId = undefined;
    }
  }
  clearIisInitializationWatchdog() {
    if (this.iisInitializationTimeoutId !== undefined) {
      clearTimeout(this.iisInitializationTimeoutId);
      this.iisInitializationTimeoutId = undefined;
    }
  }
  armIisInitializationWatchdog(timeout, phase) {
    this.clearIisInitializationWatchdog();
    this.iisInitializationTimeoutId = setTimeout(() => {
      this.iisInitializationTimeoutId = undefined;
      if (this.iisAuthResolved) {
        return;
      }
      this.iisAuthResolved = true;
      logger.warn(JSON.stringify({
        message: IIS_TIMEOUT_LOG_MESSAGE,
        phase
      }));
      this.settleIisUnavailable();
    }, timeout);
  }
  clearIisAutoLoginFrame() {
    this.clearIisAuthTimeout();
    this.iisAutoLoginFrame?.remove();
    this.iisAutoLoginFrame = null;
  }
  expireIisSid() {
    const sid = getCookie(SID_COOKIE_NAME);
    if (sid) {
      this.rejectedIisSid = sid;
    }
    createCookie(SID_COOKIE_NAME, '', -1);
  }
  async checkIisAuthentication() {
    const sid = getCookie(SID_COOKIE_NAME);
    const user = await isLoggedIn({
      skipRequestWithoutSid: true
    });
    if (isAuthenticatedOrgUser(user) && this.rejectedIisSid !== undefined && (sid === this.rejectedIisSid || getCookie(SID_COOKIE_NAME) !== sid)) {
      return null;
    }
    return user;
  }
  beginIisAuthTransaction(userId) {
    const currentUser = userStore.getState();
    const publishedIisUserId = currentUser.isGuestUser === false ? currentUser.tbidProfile?.user_id ?? currentUser.tbidUserId : undefined;
    const previousIisUserId = this.lastKnownIisUserId ?? publishedIisUserId;
    this.invalidateIisAuthTransaction();
    if (previousIisUserId !== undefined && userId !== undefined && previousIisUserId !== userId) {
      const identityReplacementLog = {
        message: 'Logout',
        timestamp: new Date().toISOString(),
        userId: previousIisUserId,
        replacementUserId: userId
      };
      logger.info(identityReplacementLog);
      this.expireIisSid();
      clearAuthenticationContext();
      userStore.reset();
    }
    if (userId !== undefined) {
      this.lastKnownIisUserId = userId;
    }
    this.iisAuthInFlight = true;
    this.iisAuthUserId = userId;
    userStore.setLoading(true);
    return this.iisAuthTransaction;
  }
  finishIisAuthTransaction(transaction) {
    if (!this.isIisAuthTransactionActive(transaction)) {
      return;
    }
    this.iisAuthInFlight = false;
    this.iisAuthUserId = undefined;
    this.clearIisAutoLoginFrame();
  }
  armIisAuthTimeout(transaction, timeout, phase) {
    this.clearIisAuthTimeout();
    this.iisAutoLoginTimeoutId = setTimeout(() => {
      if (!this.isIisAuthTransactionActive(transaction) || !this.iisAuthInFlight) {
        return;
      }
      logger.warn(JSON.stringify({
        message: IIS_TIMEOUT_LOG_MESSAGE,
        phase
      }));
      this.settleIisUnavailable();
    }, timeout);
  }
  isIisAuthTransactionActive(transaction) {
    return transaction === this.iisAuthTransaction;
  }
  settleIisGuest() {
    this.invalidateIisAuthTransaction();
    this.expireIisSid();
    clearAuthenticationContext();
    userStore.setGuestUser();
  }
  settleIisUnavailable() {
    this.invalidateIisAuthTransaction();
    userStore.setLoading(false);
  }
  async hydrateUser(userInfo, transaction) {
    if (!this.isIisAuthTransactionActive(transaction)) {
      return;
    }
    if (!userInfo.active) {
      logger.warn('IIS JS v2: received inactive user — falling back to guest user');
      this.settleIisGuest();
      return;
    }
    logger.debug(`IIS JS v2: hydrating user, userId="${userInfo.user_id ?? 'unknown'}"`);
    this.armIisAuthTimeout(transaction, DEFAULT_API_TIMEOUT, 'initial authentication');
    const user = await this.checkIisAuthentication();
    if (!this.isIisAuthTransactionActive(transaction)) {
      return;
    }
    this.clearIisAuthTimeout();
    if (isAuthenticatedOrgUser(user)) {
      logger.info(`IIS JS v2: user authenticated, userId="${userInfo.user_id ?? 'unknown'}"`);
      this.publishAuthenticatedUser(userInfo, user);
    } else if (window.location.pathname.includes(Routes.CALLBACK)) {
      // On the callback page, callback.ts is already redirecting to LoginPortal.
      // Launching a concurrent iframe would cause a CSRF mismatch on the org's Auth Provider.
      logger.debug('IIS JS v2: on callback route — skipping LoginPortal iframe, callback.ts will handle it');
    } else if (!this.iisAutoLoginFrame) {
      // Missing/invalid sid or unavailable /api/auth — establish the org session via LoginPortal.
      logger.debug('IIS JS v2: org auth unconfirmed — triggering LoginPortal iframe');
      const frame = document.createElement('iframe');
      this.iisAutoLoginFrame = frame;
      frame.src = generateTbidLoginUrl();
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      let iframeHandled = false; // guard against duplicate load fires (e.g. on redirect)
      frame.addEventListener('load', async () => {
        if (iframeHandled || !this.isIisAuthTransactionActive(transaction)) {
          return;
        }
        iframeHandled = true;
        this.iisAutoLoginFrame = null;
        frame.remove();
        this.armIisAuthTimeout(transaction, DEFAULT_API_TIMEOUT, 'post-navigation authentication');
        try {
          const authedUser = await this.checkIisAuthentication();
          if (!this.isIisAuthTransactionActive(transaction)) {
            return;
          }
          if (authedUser === null) {
            logger.warn('IIS JS v2: org session unconfirmed after LoginPortal iframe — deferring IIS profile publication');
            this.settleIisUnavailable();
          } else if (isAuthenticatedOrgUser(authedUser)) {
            logger.info(`IIS JS v2: user authenticated via iframe, userId="${userInfo.user_id ?? 'unknown'}"`);
            this.publishAuthenticatedUser(userInfo, authedUser);
          } else {
            logger.warn('IIS JS v2: TBID profile received but sid is missing or invalid — falling back to guest user');
            this.settleIisGuest();
          }
        } catch (error) {
          if (!this.isIisAuthTransactionActive(transaction)) {
            return;
          }
          handleException(error, 'tbid.hydrateUser.iframeLoginCheck');
          this.settleIisUnavailable();
        } finally {
          this.finishIisAuthTransaction(transaction);
        }
      });
      this.armIisAuthTimeout(transaction, TBID_SCRIPT_LOAD_TIMEOUT, 'LoginPortal iframe navigation');
      document.body.appendChild(frame);
    } else {
      // iframe already in flight from a prior event — do nothing, it will resolve auth
      logger.debug('IIS JS v2: LoginPortal iframe already in flight, skipping duplicate');
    }
  }
  publishAuthenticatedUser(userInfo, orgUser) {
    const {
      origin,
      type
    } = getAuthenticationContext();
    userStore.setIisAuthenticatedUser(userInfo, orgUser, type ?? '', origin);
  }
  async handleIisV2Event(event) {
    const detail = event.detail;
    const eventName = detail?.meta?.eventName;
    if (!eventName) {
      logger.warn('IIS JS v2: received tbid_auth_event with missing eventName', {
        hasEventSource: detail?.meta?.eventSource !== undefined,
        hasUserInfo: detail?.data?.userInfo !== undefined
      });
      return;
    }
    if (!isIisV2EventName(eventName)) {
      logger.warn('IIS JS v2: received tbid_auth_event with unsupported eventName');
      return;
    }
    this.iisAuthResolved = true;
    this.clearIisInitializationWatchdog();
    const {
      data
    } = detail;
    logger.debug(JSON.stringify({
      message: 'IIS JS v2: received event',
      eventName,
      eventSource: detail.meta.eventSource
    }));
    if (eventName === 'onInit') {
      const userInfo = data?.userInfo;
      if (!userInfo) {
        let isUserAuthenticated;
        try {
          isUserAuthenticated = window.iis?.isUserAuthenticated();
        } catch (error) {
          handleException(error, 'tbid.handleIisV2Event.onInit.isUserAuthenticated');
        }
        if (isUserAuthenticated === false) {
          logger.debug('IIS JS v2: onInit — SDK confirmed guest user');
          this.settleIisGuest();
          return;
        }
        if (this.iisAuthInFlight) {
          return;
        }
        const currentUser = userStore.getState();
        if (currentUser.isGuestUser === false && currentUser.tbidProfile?.active === true) {
          userStore.setLoading(false);
          return;
        }
        const transaction = this.beginIisAuthTransaction(undefined);
        this.armIisAuthTimeout(transaction, TBID_INIT_TIMEOUT, 'IIS initialization');
        return;
      }
      if (this.iisAuthInFlight && userInfo.active && userInfo.user_id !== undefined && this.iisAuthUserId === userInfo.user_id) {
        return;
      }
      const transaction = this.beginIisAuthTransaction(userInfo.user_id);
      // onInit may carry a cached userInfo (30-min sessionStorage cache); hydrate before
      // clearing isLoading so cached sessions don't land in guest state. onLogin will not
      // fire when the profile is cached.
      try {
        await this.hydrateUser(userInfo, transaction);
      } catch (error) {
        if (!this.isIisAuthTransactionActive(transaction)) {
          return;
        }
        handleException(error, 'tbid.handleIisV2Event.onInit');
        this.settleIisUnavailable();
      } finally {
        if (this.isIisAuthTransactionActive(transaction) && !this.iisAutoLoginFrame) {
          this.finishIisAuthTransaction(transaction);
          userStore.setLoading(false);
        }
      }
      return;
    }
    if (eventName === 'onLogin') {
      // onLogin fires on a cache miss (fresh login or expired cache). No isLoading guard —
      // a profileless onInit may still be waiting for this event.
      const userInfo = data?.userInfo;
      if (!userInfo) {
        logger.warn('IIS JS v2: onLogin received with no userInfo');
        if (userStore.getState().isGuestUser === false) {
          userStore.setLoading(false);
          return;
        }
        this.settleIisUnavailable();
        return;
      }
      if (userInfo.active && userInfo.user_id !== undefined && this.iisAuthInFlight && this.iisAuthUserId === userInfo.user_id) {
        logger.debug('IIS JS v2: authentication already in flight for user, skipping duplicate');
        return;
      }
      const currentUser = userStore.getState();
      const currentIisUserId = currentUser.tbidProfile?.user_id ?? currentUser.tbidUserId;
      if (userInfo.active && userInfo.user_id !== undefined && this.lastKnownIisUserId === userInfo.user_id && currentUser.isGuestUser === false && currentUser.tbidProfile?.active === true && currentIisUserId === userInfo.user_id) {
        logger.debug('IIS JS v2: user is already authenticated, skipping duplicate login event');
        return;
      }
      const transaction = this.beginIisAuthTransaction(userInfo.user_id);
      try {
        await this.hydrateUser(userInfo, transaction);
      } catch (error) {
        if (!this.isIisAuthTransactionActive(transaction)) {
          return;
        }
        handleException(error, 'tbid.handleIisV2Event.onLogin');
        this.settleIisUnavailable();
      } finally {
        if (this.isIisAuthTransactionActive(transaction) && !this.iisAutoLoginFrame) {
          this.finishIisAuthTransaction(transaction);
          userStore.setLoading(false);
        }
      }
      return;
    }
    if (eventName === 'onLogout') {
      this.invalidateIisAuthTransaction();
      this.lastKnownIisUserId = undefined;
      const userId = userStore.getState().userId;
      logger.debug(`IIS JS v2: onLogout — userId="${userId}"`);
      const logoutLog = {
        message: 'Logout',
        timestamp: new Date().toISOString(),
        userId
      };
      logger.info(logoutLog);
      userStore.reset();
      userStore.setLoading(false);
      clearAuthenticationContext();
      this.expireIisSid();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Tbid, {
  fields: ["usingV2", "iisAuthResolved", "iisAuthInFlight", "iisAutoLoginFrame", "iisAuthTransaction", "iisConnectionGeneration", "onIisV2Event"]
});
const __lwc_component_class_internal = _registerComponent(Tbid, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;

// ─── IIS JS v2 loader ────────────────────────────────────────────────────────

// Uses a <script type="module"> tag instead of dynamic import() so webpack
// never sees the URL and doesn't attempt to bundle or externalize it.
function loadIisScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('IIS_LOAD_ERROR'));
    document.head.appendChild(script);
  });
}

// ─── IIS JS v1 ───────────────────────────────────────────────────────────────

// Global handlers need to be writable for tests
Object.defineProperty(window, TBID_INIT_HANDLER, {
  value: onInitHandler,
  writable: NODE_ENV === 'test'
});
Object.defineProperty(window, TBID_LOGIN_HANDLER, {
  value: onLoginHandler,
  writable: NODE_ENV === 'test'
});
Object.defineProperty(window, TBID_LOGOUT_HANDLER, {
  value: onLogoutHandler,
  writable: NODE_ENV === 'test'
});
function initV1() {
  v1AbortController = new AbortController();
  appendMetaTag('salesforce-cache-max-age', '0');
  appendMetaTag('salesforce-brand', 'AppExchange Today');
  appendMetaTag('salesforce-community', TBID_URL);
  appendMetaTag('salesforce-mode', 'authprovider');
  appendMetaTag('salesforce-redirect-uri', `${generateBaseUrl()}/${Routes.CALLBACK}`);
  appendMetaTag('salesforce-authprovider-login', generateTbidLoginUrl());
  appendMetaTag('salesforce-authprovider-signup', generateTbidSignUpUrl());
  appendMetaTag('salesforce-init-handler', TBID_INIT_HANDLER);
  appendMetaTag('salesforce-login-handler', TBID_LOGIN_HANDLER);
  appendMetaTag('salesforce-logout-handler', TBID_LOGOUT_HANDLER);
  loadSFID(TBID_URL + TBID_AUTH_WIDGET_PATH, TBID_MAX_RETRIES, TBID_RETRY_DELAY_MS, v1AbortController.signal).catch(error => {
    if (error?.name === 'AbortError') {
      return;
    }
    if (error?.message === TBID_LOAD_TIMEOUT) {
      handleException(error, 'tbid.initV1.loadWidget', {
        level: 'warn',
        debugData: {
          timeoutMs: TBID_SCRIPT_LOAD_TIMEOUT
        }
      });
    } else {
      handleException(error, 'tbid.initV1.loadWidget');
    }
    userStore.setGuestUser();
  });
}
function onInitHandler() {
  /*
  At the user state we have the isLoading flag to indicate whether all "required" user information was fetched or not
  This method is called by TBID library (authProviderEmbeddedLogin_v1_3) for Guest User navigation
  And since we currently do not need to fetch any additional information we are indicating that the UserState its all set
  */
  if (tbidTimeoutId) {
    clearTimeout(tbidTimeoutId);
  }
  userStore.setLoading(false);
}
async function onLoginHandler(userInfo) {
  if (tbidTimeoutId) {
    clearTimeout(tbidTimeoutId);
  }
  if (!userInfo?.active) {
    logger.warn('Inactive user logged in');
    userStore.setGuestUser();
    return;
  }
  if (!userStore.getState().isLoading) {
    logger.warn('We already loaded the profile, ignore TBID login handler');
    return;
  }
  userStore.setTbidProfile(userInfo);
  const {
    origin,
    type
  } = getAuthenticationContext();
  // @ts-expect-error * Automated Type Error Fix
  userStore.setAuthenticationFlow(type, origin);
  const user = await isLoggedIn();
  if (user === null) {
    logger.warn('TBID auth endpoint unavailable — retaining the active TBID session');
    userStore.setLoading(false);
    return;
  }
  if (user?.isGuestUser === false) {
    userStore.setOrgUserInfo(user);
    return;
  }

  // Looks like we have a TBID user, but an invalid or missing sid cookie.
  // We need to auto-login the user inside an iframe and refresh the page when it completes.
  if (autoLoginFrame) {
    return;
  }
  autoLoginFrame = document.createElement('iframe');
  autoLoginFrame.src = generateTbidLoginUrl();
  autoLoginFrame.setAttribute('style', 'width: 0px; height: 0px; position: absolute');
  document.body.appendChild(autoLoginFrame);

  // The following code is largely copied from:
  // https://git.soma.salesforce.com/appexchange/tz-ecosystem/blob/develop/TrailblazerIdentity/static/assets-tbi/js/tbid-header.js#L15
  autoLoginFrame.addEventListener('load', async () => {
    if (autoLoginFrame.getAttribute('data-has-refreshed') !== 'true') {
      // Set a data attribute to avoid recursive page loading if src is set for whatever reason.
      autoLoginFrame.setAttribute('data-has-refreshed', 'true');
      const autoLoggedInUser = await isLoggedIn();
      if (autoLoggedInUser === null) {
        logger.warn('TBID auth endpoint unavailable after LoginPortal iframe');
        userStore.setLoading(false);
      } else if (autoLoggedInUser?.isGuestUser === false) {
        userStore.setOrgUserInfo(autoLoggedInUser);
        // Refresh the page to avoid running into errors with Javascript remoting when the session changes.
        window.location.reload();
      } else {
        userStore.setGuestUser();
      }
    }
  });
}
function onLogoutHandler() {
  const userId = userStore.getState().userId;

  // Log logout for security audit trail
  const logoutLog = {
    message: 'Logout',
    timestamp: new Date().toISOString(),
    userId
  };
  logger.info(logoutLog);
  userStore.reset();
  clearAuthenticationContext();
  //invalidate the org sid cookie if it exists
  createCookie('sid', '', -1);
  // TODO: Why is this here?
  window.SFIDWidget.init();
}

// Mirrors agentforceChat.loadAgentForceChatScript and tableauScript.loadTableauScript patterns.
async function loadSFID(src, retryCount = TBID_MAX_RETRIES, retryDelay = TBID_RETRY_DELAY_MS, signal) {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  let script = null;
  const load = new Promise((resolve, reject) => {
    script = document.createElement('script');
    script.onload = () => {
      tbidTimeoutId = setTimeout(() => {
        logger.warn('TBID widget did not initialize, forcing init call');
        onInitHandler();
      }, TBID_INIT_TIMEOUT);
      resolve();
    };
    script.onerror = () => reject(new Error(TBID_LOAD_ERROR));
    script.async = false;
    script.src = src;
    document.head.appendChild(script);
  });
  let timeoutHandle;
  const timeout = new Promise((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(TBID_LOAD_TIMEOUT)), TBID_SCRIPT_LOAD_TIMEOUT);
  });
  try {
    const result = await Promise.race([load, timeout]);
    clearTimeout(timeoutHandle);
    return result;
  } catch (error) {
    clearTimeout(timeoutHandle);
    // Detach handlers to prevent zombie onload from firing on an abandoned script
    if (script) {
      script.onload = null;
      script.onerror = null;
      script.remove();
    }
    if (tbidTimeoutId) {
      clearTimeout(tbidTimeoutId);
    }
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    if (retryCount > 0) {
      logger.warn(`TBID script load failed, retrying (${retryCount} attempts left)`);
      // retryCount must start at TBID_MAX_RETRIES for correct backoff exponents
      await new Promise((resolve, reject) => {
        const delayId = setTimeout(resolve, retryDelay * Math.pow(2, TBID_MAX_RETRIES - retryCount));
        signal?.addEventListener('abort', () => {
          clearTimeout(delayId);
          reject(new DOMException('Aborted', 'AbortError'));
        }, {
          once: true
        });
      });
      return loadSFID(src, retryCount - 1, retryDelay, signal);
    }
    throw error;
  }
}